"use client";

import { useLocale, useTranslations } from "next-intl";
import Link from "next/link";
import { ArrowUpRight, Mail, MapPin, Phone } from "lucide-react";
import { RegionalAirportConfig } from "./types";
import { type Locale } from "@/i18n/config";
import { pickLocalized } from "./types";

interface RegionalFooterProps {
  config: RegionalAirportConfig;
}

export default function RegionalFooter({ config }: RegionalFooterProps) {
  const locale = useLocale() as Locale;
  const t = useTranslations("regionalLayout.footer");
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#102542] text-white">
      {/* Contenido principal */}
      <div className="max-w-[80%] mx-auto py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
          {/* Aeropuerto */}
          <div>
            <p className="text-xl font-bold text-white tracking-tight">
              {config.code}
            </p>
            <p className="text-sm text-white/60 mt-1 mb-4">
              {pickLocalized(config.name, locale)}
            </p>
            <p className="text-sm text-white/60 leading-relaxed">
              {pickLocalized(config.contact.address, locale)}
            </p>
          </div>

          {/* Contacto */}
          <div>
            <p className="text-xs font-mono uppercase tracking-wider text-white/40 mb-4">
              {t("contact")}
            </p>
            <ul className="space-y-3">
              <li>
                <a
                  href={`tel:${config.contact.phone}`}
                  className="flex items-center gap-3 text-sm text-white/60 hover:text-white transition-colors"
                >
                  <Phone className="w-4 h-4 text-white/40" />
                  <span className="tabular-nums">{config.contact.phone}</span>
                </a>
              </li>
              {config.contact.email && (
                <li>
                  <a
                    href={`mailto:${config.contact.email}`}
                    className="flex items-center gap-3 text-sm text-white/60 hover:text-white transition-colors"
                  >
                    <Mail className="w-4 h-4 text-white/40" />
                    {config.contact.email}
                  </a>
                </li>
              )}
              <li className="flex items-start gap-3 text-sm text-white/60">
                <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-white/40" />
                <span>{pickLocalized(config.contact.address, locale)}</span>
              </li>
            </ul>
          </div>

          {/* Enlaces rápidos */}
          <div>
            <p className="text-xs font-mono uppercase tracking-wider text-white/40 mb-4">
              {t("quickLinks")}
            </p>
            <ul className="space-y-3">
              {config.navLinks.slice(0, 5).map((link, index) => (
                <li key={index}>
                  <Link
                    href={`/${locale}${link.href}`}
                    className="text-sm text-white/60 hover:text-white transition-colors"
                  >
                    {pickLocalized(link.label, locale)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Red de aeropuertos */}
          <div>
            <p className="text-xs font-mono uppercase tracking-wider text-white/40 mb-4">
              {t("airports")}
            </p>
            <ul className="space-y-3">
              <li>
                <Link
                  href={`/${locale}`}
                  className="group inline-flex items-center gap-1.5 text-sm text-white/60 hover:text-white transition-colors"
                >
                  {t("mainAirport")}
                  <ArrowUpRight className="w-3.5 h-3.5 text-white/40 transition-colors group-hover:text-white" />
                </Link>
              </li>
              <li>
                <Link
                  href={`/${locale}/aeropuertos`}
                  className="group inline-flex items-center gap-1.5 text-sm font-semibold text-white transition-opacity hover:opacity-80"
                >
                  {t("backToSelector")}
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Barra inferior */}
      <div className="border-t border-white/10">
        <div className="max-w-[80%] mx-auto py-6 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-sm text-white/40">
            <span className="font-mono tabular-nums">© {currentYear}</span>{" "}
            {pickLocalized(config.name, locale)}. {t("rights")}.
          </p>
          <p className="text-sm text-white/40">{t("operatedBy")}</p>
        </div>
      </div>
    </footer>
  );
}
